import React from 'react'
import { Grid, InputNumber, message, Pagination, Skeleton, Tooltip } from 'antd'
import { DownOutlined, QuestionCircleOutlined, UpOutlined } from '@ant-design/icons'
import i from '../../translations/i'
import './card_list.less'

// Карточный список: шапка с заголовком и счётчиком, панель фильтров, карточки, пустое состояние, пагинация.
// Пример — src/front/marketing/pushes/Pushes.tsx, src/front/users/Users.tsx.

export type ListTone = 'primary' | 'success' | 'warning' | 'danger' | 'info'
export type MediaTone = 'violet' | 'blue' | 'cyan' | 'green' | 'orange' | 'red' | 'gray'

export const EMPTY_VALUE = '—'

// Телефон и планшет: таблицы со многими колонками показываем карточками, чтобы не было прокрутки вбок.
// До первого замера (undefined) считаем экран широким — на телефоне переключится до отрисовки
export const useNarrowScreen = () => Grid.useBreakpoint().lg === false

// Бэкенд иногда отдаёт пустое значение строкой "null"
export const cleanText = (value: any): string => {
    if (value === null || value === undefined) return ''
    const text = `${value}`.trim()
    return text === 'null' || text === 'undefined' ? '' : text
}

// ---------- Миниатюра ----------

// Необязательна: без неё карточка перестраивается в две колонки
export const ListMedia = ({ image, icon, tone }: {
    image?: string | null
    icon?: React.ReactNode
    tone?: MediaTone
}) => (
    <div className={`cl-media${tone && tone !== 'violet' ? ` cl-media--${tone}` : ''}`}>
        {image ? <img src={image} alt="" loading="lazy" /> : icon}
    </div>
)

// ---------- Чип ----------

export const ListChip = ({ icon, tone, tooltip, copy, onClick, active, children }: {
    icon?: React.ReactNode
    tone?: ListTone
    tooltip?: React.ReactNode
    // Значение, которое копируется по клику (email, телефон, артикул)
    copy?: string
    // Чип-кнопка, например быстрый фильтр; active — выбран
    onClick?: () => void
    active?: boolean
    children: React.ReactNode
}) => {
    const className = [
        'cl-chip',
        tone && `cl-chip--${tone}`,
        (copy || onClick) && 'cl-chip--action',
        active && 'cl-chip--active',
    ].filter(Boolean).join(' ')

    const handleClick = copy
        ? () => navigator.clipboard?.writeText(copy).then(() => message.success(i.t('list:copied')))
        : onClick

    const chip = (
        <span className={className} onClick={handleClick}>
            {icon}
            <span className="cl-chip__label">{children}</span>
        </span>
    )

    const title = tooltip ?? (copy ? i.t('list:copy') : undefined)
    return title ? <Tooltip title={title}>{chip}</Tooltip> : chip
}

// ---------- Метрика ----------

export interface ListMetricProps {
    value: React.ReactNode
    label: React.ReactNode
    hint?: React.ReactNode
    // Сколько колонок занимает ячейка: например, одна заглушка вместо трёх метрик
    span?: number
    tone?: ListTone
    dashed?: boolean
    // Ячейка-ссылка, например на список записей за цифрой
    href?: string
}

export const ListMetric = ({ value, label, hint, span = 1, tone, dashed, href }: ListMetricProps) => {
    const empty = value === null || value === undefined || value === '' || value === EMPTY_VALUE
    const className = [
        'cl-metric',
        empty && 'cl-metric--empty',
        dashed && 'cl-metric--dashed',
        tone && `cl-metric--${tone}`,
        href && 'cl-metric--link',
    ].filter(Boolean).join(' ')

    const style = span > 1 ? { gridColumn: `span ${span}` } : undefined
    const inner = <>
        <span className="cl-metric__value">{empty ? EMPTY_VALUE : value}</span>
        <span className="cl-metric__label">{label}</span>
    </>
    const content = href
        ? <a className={className} style={style} href={href}>{inner}</a>
        : <div className={className} style={style}>{inner}</div>

    return hint ? <Tooltip title={hint}>{content}</Tooltip> : content
}

// ---------- Карточка ----------

export interface ListCardProps {
    media?: React.ReactNode
    title: React.ReactNode
    // Полный заголовок во всплывающей подсказке, если он обрезан
    titleHint?: string
    // Бейджи сразу после заголовка
    titleExtra?: React.ReactNode
    // Открыть запись (обычно редактирование) кликом по любому месту карточки, кроме кнопок и ссылок.
    // href — переход по ссылке (Cmd/Ctrl+клик откроет в новой вкладке), onOpen — своё действие, например модалка
    href?: string
    onOpen?: () => void
    subtitle?: React.ReactNode
    text?: React.ReactNode
    chips?: React.ReactNode
    metrics?: ListMetricProps[]
    // Ширина колонки метрики, px
    metricWidth?: number
    // Сколько колонок в сетке метрик: лишние ячейки переносятся на следующую строку.
    // Без него все метрики в одну строку
    metricColumns?: number
    actions?: React.ReactNode
    muted?: boolean
    selected?: boolean
    // Требует внимания, например непросмотренный заказ: цветная полоса слева и подсветка
    accent?: boolean
}

export const ListCard = ({
    media, title, titleHint, titleExtra, href, onOpen, subtitle, text, chips,
    metrics, metricWidth = 96, metricColumns, actions, muted, selected, accent,
}: ListCardProps) => {
    const clickable = !!(href || onOpen)

    const className = [
        'cl-card',
        clickable && 'cl-card--clickable',
        !media && 'cl-card--no-media',
        muted && 'cl-card--muted',
        selected && 'cl-card--selected',
        accent && 'cl-card--accent',
    ].filter(Boolean).join(' ')

    const metricCols = metricColumns ?? (metrics ?? []).reduce((sum, metric) => sum + (metric.span ?? 1), 0)
    const metricsStyle = { '--cl-metric-cols': metricCols, '--cl-metric-w': `${metricWidth}px` } as React.CSSProperties

    const onClick = (e: React.MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement
        // Клики из порталов (Popconfirm, подсказки) всплывают по React-дереву — они не про карточку
        if (!clickable || !e.currentTarget.contains(target)) return
        // У кнопок, ссылок, полей и копируемых чипов своё действие
        if (target.closest('a, button, input, textarea, label, .cl-chip--action, .ant-select, .ant-switch, .ant-checkbox-wrapper')) return
        // Выделяли текст, чтобы скопировать, — не уходим со страницы
        if (window.getSelection()?.toString()) return

        if (onOpen) onOpen()
        else if (e.metaKey || e.ctrlKey) window.open(href, '_blank')
        else window.location.href = href!
    }

    return (
        <article className={className} onClick={onClick}>
            {media && <div className="cl-card__media">{media}</div>}

            <div className="cl-card__body">
                <div className="cl-card__title-row">
                    {href
                        ? <a className="cl-card__title cl-card__link" href={href} title={titleHint}>{title}</a>
                        : <span className="cl-card__title" title={titleHint}>{title}</span>
                    }
                    {titleExtra && <span className="cl-card__title-extra">{titleExtra}</span>}
                </div>
                {subtitle && <div className="cl-card__subtitle">{subtitle}</div>}
                {text && <div className="cl-card__text">{text}</div>}
                {chips && <div className="cl-card__chips">{chips}</div>}
            </div>

            {(metricCols > 0 || actions) &&
                <div className="cl-card__side">
                    {metricCols > 0 &&
                        <div className="cl-card__metrics" style={metricsStyle}>
                            {metrics!.map((metric, index) => <ListMetric key={index} {...metric} />)}
                        </div>
                    }
                    {actions && <div className="cl-card__actions">{actions}</div>}
                </div>
            }
        </article>
    )
}

// ---------- Пустое состояние ----------

export interface ListEmptyProps {
    icon?: React.ReactNode
    title: React.ReactNode
    text?: React.ReactNode
    action?: React.ReactNode
}

export const ListEmpty = ({ icon, title, text, action }: ListEmptyProps) => (
    <div className="cl-empty">
        {icon && <span className="cl-empty__icon">{icon}</span>}
        <h3 className="cl-empty__title">{title}</h3>
        {text && <div className="cl-empty__text">{text}</div>}
        {action}
    </div>
)

// ---------- Панель фильтров ----------

export const ListToolbar = ({ children }: { children: React.ReactNode }) => (
    <div className="cl-toolbar">{children}</div>
)

// Поле фиксированной ширины внутри панели; на телефоне растягивается на всю строку.
// label — подпись над полем, hint — пояснение по наведению на значок рядом с подписью, wide — поле шире обычного
export const ListToolbarField = ({ label, hint, wide, children }: {
    label?: React.ReactNode
    hint?: React.ReactNode
    wide?: boolean
    children: React.ReactNode
}) => (
    <div className={`cl-toolbar__field${wide ? ' cl-toolbar__field--wide' : ''}`}>
        {label &&
            <div className="cl-toolbar__label">
                {label}
                {hint && <Tooltip title={hint}><QuestionCircleOutlined className="cl-toolbar__hint" /></Tooltip>}
            </div>
        }
        {children}
    </div>
)

export const ListToolbarSpacer = () => <div className="cl-toolbar__spacer" />

// Перенос на новую строку с тонкой линией: отделяет, например, поиск от аналитических фильтров
// С onToggle заголовок — кнопка, которая сворачивает секцию (поля под ней показывает сам экран по open);
// count — сколько фильтров задано в свёрнутой секции
export const ListToolbarBreak = ({ title, extra, open, onToggle, count }: {
    title?: React.ReactNode
    extra?: React.ReactNode
    open?: boolean
    onToggle?: () => void
    count?: number
}) => (
    <div className="cl-toolbar__break">
        {title && (onToggle
            ? <button type="button" className="cl-toolbar__section cl-toolbar__toggle" onClick={onToggle} aria-expanded={!!open}>
                {title}
                {!!count && <span className="cl-toolbar__toggle-count">{count}</span>}
                {open ? <UpOutlined /> : <DownOutlined />}
            </button>
            : <span className="cl-toolbar__section">{title}</span>
        )}
        {extra}
    </div>
)

// Строка чипов во всю ширину панели, например быстрые фильтры
export const ListToolbarChips = ({ label, children }: { label?: React.ReactNode, children: React.ReactNode }) => (
    <div className="cl-toolbar__chips">
        {label && <span className="cl-toolbar__chips-label">{label}</span>}
        {children}
    </div>
)

// Диапазон «от — до» целых чисел, границы включительно; null — без ограничения
export const ListRange = ({ from, to, onChange, min = 0, max }: {
    from: number | null
    to: number | null
    onChange: (from: number | null, to: number | null) => void
    min?: number
    max?: number
}) => (
    <div className="cl-range">
        <InputNumber
            prefix={i.t('list:from')}
            min={min}
            max={max}
            precision={0}
            value={from}
            onChange={value => onChange(value ?? null, to)}
        />
        <InputNumber
            prefix={i.t('list:to')}
            min={min}
            max={max}
            precision={0}
            value={to}
            onChange={value => onChange(from, value ?? null)}
        />
    </div>
)

// ---------- Список ----------

export interface CardListPagination {
    current: number
    pageSize: number
    total: number
    onChange: (page: number, pageSize: number) => void
    showSizeChanger?: boolean
}

export interface CardListProps<T> {
    items: T[]
    renderItem: (item: T, index: number) => React.ReactNode
    rowKey?: (item: T) => React.Key
    title?: React.ReactNode
    // Всего записей — показывается рядом с заголовком
    count?: number
    // Справа от заголовка: переключатели, кнопки
    extra?: React.ReactNode
    // Над списком: поиск и фильтры (обычно ListToolbar)
    toolbar?: React.ReactNode
    // Пока первая загрузка не пришла — скелетоны вместо пустого состояния
    loading?: boolean
    empty?: ListEmptyProps
    pagination?: CardListPagination
}

export const CardList = <T,>({
    items, renderItem, rowKey = (item: any) => item.id, title, count, extra, toolbar, loading, empty, pagination,
}: CardListProps<T>) => {
    // Переключатель размера страницы должен оставаться доступным, даже когда страница одна
    const showPagination = pagination && pagination.total > (pagination.showSizeChanger ? 10 : pagination.pageSize)

    return (
        <section className="cl-list">
            {toolbar}

            {(title || extra || (count !== undefined && count > 0)) &&
                <div className="cl-list__head">
                    <div className="cl-list__heading">
                        {title && <h3 className="cl-list__title">{title}</h3>}
                        {count !== undefined && count > 0 && <span className="cl-list__count">{i.t('list:total', { total: count.toLocaleString() })}</span>}
                    </div>
                    {extra}
                </div>
            }

            {loading && items.length === 0
                ? [0, 1, 2].map(index => (
                    <div key={index} className="cl-card cl-card--no-media">
                        <Skeleton active title paragraph={{ rows: 1 }} />
                    </div>
                ))
                : items.length === 0
                    ? empty && <ListEmpty {...empty} />
                    : items.map((item, index) => <React.Fragment key={rowKey(item)}>{renderItem(item, index)}</React.Fragment>)
            }

            {showPagination &&
                <div className="cl-list__pagination">
                    <Pagination
                        current={pagination!.current}
                        total={pagination!.total}
                        pageSize={pagination!.pageSize}
                        showSizeChanger={pagination!.showSizeChanger ?? false}
                        onChange={pagination!.onChange}
                    />
                </div>
            }
        </section>
    )
}
